import type { InjectedRouter } from 'js/lib/connectToRouter';

import {
  EmailIcon,
  HomeIcon,
  HonorsAssignmentIcon,
  MembersIcon,
  ReadingIcon,
  ReviewIcon,
  SettingsIcon,
  TrendingIcon,
} from '@coursera/cds-icons';

import type { SideNavIconComponent } from 'bundles/enterprise-admin-navigation/components/side-nav/base/types';
import {
  ANALYTICS_PATH,
  CONTRACTS_PATH,
  CUSTOM_COURSES_PATH,
  HOME_PATH,
  LEARNERS_PATH,
  MESSAGES_PATH,
  ORG_SKILLSETS_PATH,
  ORG_SKILLS_PATH,
  PROGRAMS_PATH,
  SETTINGS_PATH,
  USERS_PATH,
} from 'bundles/enterprise-admin-navigation/utils/pathConstants';
import {
  ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN as SUB,
  ENTERPRISE_ADMIN_PERMISSION_VERB as VERB,
} from 'bundles/enterprise-admin-roles/types';
import type { RequiredPermission } from 'bundles/enterprise-admin-roles/types';
import { showOrgLevelSkillsForOrg } from 'bundles/enterprise-admin-skills/utils/common';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';

import _t from 'i18n!nls/enterprise-admin-navigation';

/**
 * Runtime context resolved once per render by `useAdminSideNavItems`.
 * Passed to every `shouldShow` predicate and override so items can make
 * data-driven decisions (e.g. "show this only if user has permission X").
 *
 * Built from existing hooks (router, permissions, flags) so consumers stay
 * simple — no need to thread dependencies individually to every item.
 *
 * Properties:
 * - router: Router instance for navigation checks and active state.
 * - thirdPartyOrganization: Current organization data.
 * - enableSkillsPlanningForOrg: Feature flag for skills planning.
 * - enableNewLearnersPage: Feature flag for new learners page.
 * - hasPermission: Function to check if user has specific permission.
 * - isOrganizationDomainAdmin: Whether user is organization domain admin.
 * - canUseCourseBuilder: Whether user can use course builder.
 * - isSuperUserOrEnterpriseOutsourcing: Enterprise-level permissions check.
 * - isFreemiumOrgAndCanOnlyManagePrograms: Freemium org limitation flag.
 * - locationSearchString: Query params carried over during navigation (ref=ADMIN_HOME_V2_REF stripped).
 */
export type AdminSideNavRuntimeContext = {
  router: InjectedRouter;
  thirdPartyOrganization: AdminDashboardOrg;
  enableSkillsPlanningForOrg: boolean;
  enableNewLearnersPage: boolean;
  hasPermission: (perm: RequiredPermission) => boolean;
  isOrganizationDomainAdmin: boolean;
  canUseCourseBuilder: boolean;
  isSuperUserOrEnterpriseOutsourcing: boolean;
  isFreemiumOrgAndCanOnlyManagePrograms: boolean;
  locationSearchString: string;
};

/**
 * AdminSideNavSubItemConfig defines the shape of each sub-nav item.
 *
 * Sub-nav rows are produced from a parent `children` entry and mirror `SideNavSubItem`
 * structure but without icon or nested children properties.
 *
 * Properties:
 * - id: Unique string identifier for the sub-item.
 * - getLabel: Function that receives runtime context and returns the display string.
 * - href: Route template (':thirdPartySlug' substituted at runtime).
 * - requiredPermissions: (Optional) Permission(s) needed to see/select this item.
 * - shouldShow: (Optional) Predicate that decides visibility based on runtime context.
 * - isActiveOverride: (Optional) Custom active-state predicate. Defaults to router.isActive(href).
 */
export type AdminSideNavSubItemConfig = {
  id: string;
  getLabel: (runtimeContext: AdminSideNavRuntimeContext) => string;
  href: string;
  requiredPermissions?: RequiredPermission;
  shouldShow?: (runtimeContext: AdminSideNavRuntimeContext) => boolean;
  isActiveOverride?: (runtimeContext: AdminSideNavRuntimeContext, resolvedHref: string) => boolean;
};

/**
 * AdminSideNavItemConfig defines the shape of each side-nav item.
 *
 * Pure data structure — no React, no closures, no action callbacks. Walked at runtime by
 * `useAdminSideNavItems`. Adding/removing/reordering/nesting an item is a one-array edit
 * on the `sideNavConfigDefs` constant.
 *
 * Each item's `shouldShow` predicate receives the runtime context and decides whether
 * to render the item. Returning false drops the item from the list.
 *
 * Properties:
 * - id: Unique string identifier for the item.
 * - getLabel: Function that receives runtime context and returns the display string.
 * - icon: Icon component displayed alongside the label.
 * - href: Static route template (':thirdPartySlug' substituted at runtime).
 * - hrefOverride: (Optional) Dynamic href that wins over `href` when set (e.g., Skills vs SkillSets).
 * - requiredPermissions: (Optional) Permission(s) needed to see/select this item.
 * - shouldShow: (Optional) Predicate that decides visibility based on runtime context.
 * - isActiveOverride: (Optional) Custom active-state predicate (e.g., Programs vs /contracts mutex). Defaults to router.isActive(href).
 * - slot: (Optional) Placement on the side-nav. Defaults to 'side-nav'. Can be 'side-nav-bottom' for bottom items.
 * - children: (Optional) Sub-nav links rendered via `SideNavExpandableItem`.
 */
export type AdminSideNavItemConfig = {
  id: string;
  getLabel: (runtimeContext: AdminSideNavRuntimeContext) => string;
  icon: SideNavIconComponent;
  href: string;
  hrefOverride?: (runtimeContext: AdminSideNavRuntimeContext) => string;
  requiredPermissions?: RequiredPermission;
  shouldShow?: (runtimeContext: AdminSideNavRuntimeContext) => boolean;
  isActiveOverride?: (runtimeContext: AdminSideNavRuntimeContext, resolvedHref: string) => boolean;
  slot?: 'side-nav' | 'side-nav-bottom';
  children?: AdminSideNavSubItemConfig[];
};

/**
 * The single source of truth for the new admin side-nav items.
 *
 * To add an item: drop a new entry into the array (with a `shouldShow` if it should
 * be conditionally visible). To remove one: delete the entry. To nest: add
 * `children`.
 *
 * Exported as a constant to prevent array recreation on every hook invocation.
 * Labels are resolved at render time via the config predicates and resolvers.
 *
 * Note: _t() calls within getLabel functions execute at hook runtime (both server/client),
 * ensuring consistent SSR behavior. The eslint warning is overly cautious for this pattern.
 */
export const sideNavConfigDefs: AdminSideNavItemConfig[] = [
  /* eslint-disable no-restricted-syntax */
  {
    id: 'ADMIN_HOME',
    getLabel: () => _t('Home'),
    icon: HomeIcon,
    href: HOME_PATH,
  },
  {
    id: 'CUSTOM_COURSES',
    getLabel: () => _t('Custom Courses'),
    icon: ReviewIcon,
    href: CUSTOM_COURSES_PATH,
    shouldShow: (runtimeContext) => runtimeContext.canUseCourseBuilder,
    requiredPermissions: { domain: 'ANY_PROGRAM', subdomain: SUB.CUSTOM_COURSES, verb: VERB.VIEW },
  },
  {
    id: 'PROGRAMS',
    getLabel: () => _t('Programs'),
    icon: ReadingIcon,
    href: PROGRAMS_PATH,
    isActiveOverride: (runtimeContext, href) =>
      runtimeContext.router.isActive(href) && !runtimeContext.router.location.pathname.includes('/contracts'),
  },
  {
    id: 'ORG_TSPS',
    // Skills replaces SkillSets for orgs flagged via showOrgLevelSkillsForOrg.
    getLabel: (runtimeContext) =>
      showOrgLevelSkillsForOrg(runtimeContext.thirdPartyOrganization.id) ? _t('Skills') : _t('SkillSets'),
    icon: HonorsAssignmentIcon,
    href: ORG_SKILLSETS_PATH,
    hrefOverride: (runtimeContext) =>
      showOrgLevelSkillsForOrg(runtimeContext.thirdPartyOrganization.id) ? ORG_SKILLS_PATH : ORG_SKILLSETS_PATH,
    shouldShow: (runtimeContext) => runtimeContext.enableSkillsPlanningForOrg,
    requiredPermissions: { domain: 'ORGANIZATION', subdomain: SUB.SKILLSETS, verb: VERB.EDIT },
  },
  {
    id: 'CONTRACTSV2',
    getLabel: () => _t('Contracts'),
    icon: MembersIcon,
    href: CONTRACTS_PATH,
    shouldShow: (runtimeContext) => runtimeContext.isSuperUserOrEnterpriseOutsourcing,
  },
  {
    id: 'LEARNERS',
    getLabel: () => _t('Learners'),
    icon: MembersIcon,
    href: LEARNERS_PATH,
    shouldShow: (runtimeContext) => runtimeContext.enableNewLearnersPage,
    requiredPermissions: { domain: 'ANY_PROGRAM', subdomain: SUB.LEARNERS, verb: VERB.MANAGE },
  },
  {
    id: 'USERS',
    getLabel: () => _t('Users'),
    icon: MembersIcon,
    href: USERS_PATH,
    shouldShow: (runtimeContext) => !runtimeContext.enableNewLearnersPage,
    requiredPermissions: { domain: 'ANY_PROGRAM', subdomain: SUB.LEARNERS, verb: VERB.MANAGE },
  },
  {
    id: 'MESSAGES',
    getLabel: () => _t('Messages'),
    icon: EmailIcon,
    href: MESSAGES_PATH,
    requiredPermissions: { domain: 'ANY_PROGRAM', subdomain: SUB.COMMUNICATIONS, verb: VERB.VIEW },
  },
  {
    id: 'ANALYTICS',
    getLabel: () => _t('Analytics'),
    icon: TrendingIcon,
    href: ANALYTICS_PATH,
    shouldShow: (runtimeContext) => !runtimeContext.isFreemiumOrgAndCanOnlyManagePrograms,
    requiredPermissions: { domain: 'ANY_PROGRAM', subdomain: SUB.ANALYTICS, verb: VERB.VIEW },
  },
  {
    id: 'SETTINGS',
    getLabel: () => _t('Settings'),
    icon: SettingsIcon,
    href: SETTINGS_PATH,
    slot: 'side-nav-bottom',
    shouldShow: (runtimeContext) => runtimeContext.isOrganizationDomainAdmin,
  },
  /* eslint-enable no-restricted-syntax */
];
